// ---------------------------------------------------------------------------
// The Summary step's rules (R17b)
//
// The checklist, the coverage gaps of a cross-channel campaign with the
// fallbacks picked for them, and when "Finish setup" may be pressed. Taken
// out of wizard/wiz-summary.ts unchanged, so the step's rebuild (R17n) redoes
// only the markup. Plain TypeScript.
// ---------------------------------------------------------------------------

import type { CampaignDraft, ChannelFallback } from '../types.js';

/** One line of the checklist. */
export interface CheckItem {
  label: string;
  ok: boolean;
  detail: string;
}

/** The checklist: every part of the campaign, done or what is missing. */
export const validateDraft = (draft: CampaignDraft): CheckItem[] => {
  const checks: CheckItem[] = [];

  // Campaign type is now derived from the sequence (see `deriveCampaignType`),
  // so we just surface the resulting label instead of asking the user to pick
  // it. The check always passes unless the sequence is empty (covered by the
  // "Sequence" check further down).
  if (draft.campaignType) {
    checks.push({
      label: 'Campaign Type',
      ok: true,
      detail:
        draft.campaignType === 'multi'
          ? 'Cross-Channel (Email + LinkedIn) — derived from sequence'
          : `${draft.campaignType.charAt(0).toUpperCase() + draft.campaignType.slice(1)} — derived from sequence`,
    });
  }

  // Only the product/service name is required; the description and talking
  // points are optional context, so the offering is valid with just a name.
  const hasOffering = !!draft.offering.name.trim();
  checks.push({
    label: 'Offering',
    ok: hasOffering,
    detail: hasOffering ? draft.offering.name : 'Missing product or service name',
  });

  const hasApolloSourcing = !!draft.apolloSearchInput;
  checks.push({
    label: 'Leads',
    ok: draft.leads.length > 0 || hasApolloSourcing,
    detail:
      draft.leads.length > 0
        ? `${draft.leads.length} leads`
        : hasApolloSourcing
          ? 'Apollo sourcing configured'
          : 'No leads uploaded',
  });

  const hasDays = draft.schedule.sendDays.length > 0;
  checks.push({
    label: 'Schedule',
    ok: hasDays,
    detail: hasDays ? `${draft.schedule.sendDays.length} days, limit ${draft.schedule.dailyLimit}/day` : 'No send days',
  });

  checks.push({
    label: 'Sequence',
    ok: draft.sequence.length > 0,
    detail: draft.sequence.length > 0 ? `${draft.sequence.length} steps` : 'No steps defined',
  });

  checks.push({
    label: 'Sales Representatives',
    ok:
      draft.aiEmployeeAssignments.length > 0 &&
      draft.aiEmployeeAssignments.reduce((s, a) => s + a.percentage, 0) === 100 &&
      draft.aiEmployeeAssignments.every(a => a.percentage > 0),
    detail:
      draft.aiEmployeeAssignments.length === 0
        ? 'No Sales Representative selected'
        : draft.aiEmployeeAssignments.reduce((s, a) => s + a.percentage, 0) !== 100
          ? `Percentages must total 100% (currently ${draft.aiEmployeeAssignments.reduce((s, a) => s + a.percentage, 0)}%)`
          : draft.aiEmployeeAssignments.some(a => a.percentage <= 0)
            ? 'Each assigned Sales Representative must have a share > 0%'
            : `${draft.aiEmployeeAssignments.length} Sales Rep${draft.aiEmployeeAssignments.length > 1 ? 's' : ''} (${draft.aiEmployeeAssignments.map(a => `${a.percentage}%`).join(' / ')})`,
  });

  if (draft.campaignType === 'email') {
    const missing = draft.aiEmployeeAssignments.filter(
      a => (draft.emailAccountsByAie[a.aiEmployeeId] ?? []).length === 0,
    );
    checks.push({
      label: 'Email Accounts',
      ok: draft.aiEmployeeAssignments.length > 0 && missing.length === 0,
      detail:
        missing.length > 0
          ? `${missing.length} Sales Rep(s) without selected accounts`
          : `${draft.emailAccounts.length} account(s) across ${draft.aiEmployeeAssignments.length} Sales Rep(s)`,
    });
  } else if (draft.campaignType === 'linkedin') {
    const missing = draft.aiEmployeeAssignments.filter(
      a => (draft.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length === 0,
    );
    checks.push({
      label: 'LinkedIn Accounts',
      ok: draft.aiEmployeeAssignments.length > 0 && missing.length === 0,
      detail:
        missing.length > 0
          ? `${missing.length} Sales Rep(s) without selected accounts`
          : `${draft.linkedinAccountIds.length} account(s) across ${draft.aiEmployeeAssignments.length} Sales Rep(s)`,
    });
  } else if (draft.campaignType === 'multi') {
    const hasEmailStep = draft.sequence.some(s => s.channel === 'email');
    const hasLiStep = draft.sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm');
    const missingEmail = hasEmailStep
      ? draft.aiEmployeeAssignments.filter(a => (draft.emailAccountsByAie[a.aiEmployeeId] ?? []).length === 0)
      : [];
    const missingLi = hasLiStep
      ? draft.aiEmployeeAssignments.filter(a => (draft.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length === 0)
      : [];
    // In multi-mode a Sales Rep only fully-fails validation if it has neither channel
    // (then it cannot send anything at all). Partial coverage is allowed but
    // surfaced via the dedicated Channel Coverage warning panel.
    const orphanSalesReps = draft.aiEmployeeAssignments.filter(a => {
      const noEmail = (draft.emailAccountsByAie[a.aiEmployeeId] ?? []).length === 0;
      const noLi = (draft.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length === 0;
      return noEmail && noLi;
    });
    if (hasEmailStep) {
      const partial = missingEmail.length > 0 && missingEmail.length < draft.aiEmployeeAssignments.length;
      checks.push({
        label: 'Email Accounts',
        ok: draft.aiEmployeeAssignments.length > 0 && missingEmail.length < draft.aiEmployeeAssignments.length,
        detail:
          missingEmail.length === draft.aiEmployeeAssignments.length
            ? 'No Sales Representative has an email account configured'
            : partial
              ? `${draft.emailAccounts.length} account(s) — ${missingEmail.length} Sales Rep(s) without email (see coverage warning)`
              : `${draft.emailAccounts.length} account(s) across ${draft.aiEmployeeAssignments.length} Sales Rep(s)`,
      });
    }
    if (hasLiStep) {
      const partial = missingLi.length > 0 && missingLi.length < draft.aiEmployeeAssignments.length;
      checks.push({
        label: 'LinkedIn Accounts',
        ok: draft.aiEmployeeAssignments.length > 0 && missingLi.length < draft.aiEmployeeAssignments.length,
        detail:
          missingLi.length === draft.aiEmployeeAssignments.length
            ? 'No Sales Representative has a LinkedIn account configured'
            : partial
              ? `${draft.linkedinAccountIds.length} account(s) — ${missingLi.length} Sales Rep(s) without LinkedIn (see coverage warning)`
              : `${draft.linkedinAccountIds.length} account(s) across ${draft.aiEmployeeAssignments.length} Sales Rep(s)`,
      });
    }
    if (orphanSalesReps.length > 0) {
      checks.push({
        label: 'Sales Rep Channels',
        ok: false,
        detail: `${orphanSalesReps.length} Sales Rep(s) have no email AND no LinkedIn account — they cannot send anything.`,
      });
    }
    if (!hasEmailStep && !hasLiStep) {
      checks.push({
        label: 'Channels',
        ok: false,
        detail: 'Cross-channel campaign needs at least one Email or LinkedIn step.',
      });
    }
  }

  checks.push({ label: 'Auto-Reply', ok: true, detail: draft.autoReply ? 'Enabled' : 'Disabled' });

  return checks;
};

/**
 * A Sales Rep that has at least one channel but is missing the other one the
 * sequence uses (cross-channel campaigns only), with the leads it would skip,
 * estimated from its share.
 */
export interface CoverageGap {
  aiEmployeeId: string;
  missing: 'email' | 'linkedin';
  percentage: number;
  estimatedLeads: number;
}

export function coverageGaps(draft: CampaignDraft): CoverageGap[] {
  if (draft.campaignType !== 'multi') return [];
  const hasEmailStep = draft.sequence.some(s => s.channel === 'email');
  const hasLiStep = draft.sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm');
  const totalLeads = draft.leads.length;
  const gaps: CoverageGap[] = [];
  for (const a of draft.aiEmployeeAssignments) {
    const emails = draft.emailAccountsByAie[a.aiEmployeeId] ?? [];
    const linkedins = draft.linkedinAccountIdsByAie[a.aiEmployeeId] ?? [];
    const estimated = Math.round((a.percentage / 100) * totalLeads);
    if (hasEmailStep && emails.length === 0 && linkedins.length > 0) {
      gaps.push({
        aiEmployeeId: a.aiEmployeeId,
        missing: 'email',
        percentage: a.percentage,
        estimatedLeads: estimated,
      });
    }
    if (hasLiStep && linkedins.length === 0 && emails.length > 0) {
      gaps.push({
        aiEmployeeId: a.aiEmployeeId,
        missing: 'linkedin',
        percentage: a.percentage,
        estimatedLeads: estimated,
      });
    }
  }
  return gaps;
}

/** The key a gap's fallback pick is kept under: `${sourceAieId}::${channel}`. */
export const gapKey = (g: Pick<CoverageGap, 'aiEmployeeId' | 'missing'>): string => `${g.aiEmployeeId}::${g.missing}`;

/**
 * The picks a campaign already carries (an edit of a prepared campaign, a
 * duplicated one), keyed as `gapKey`. Without them the warning always reset to
 * "Skip" although the draft carries channelFallbacks.
 */
export function persistedFallbackChoices(draft: CampaignDraft): Record<string, string> {
  const persisted: Record<string, string> = {};
  for (const f of draft.channelFallbacks ?? []) {
    if (f?.sourceAiEmployeeId && f?.channel && f?.fallbackAiEmployeeId) {
      persisted[`${f.sourceAiEmployeeId}::${f.channel}`] = f.fallbackAiEmployeeId;
    }
  }
  return persisted;
}

/** The fallbacks the prepare payload carries: one per gap a rep was picked for ('' is "skip"). */
export const channelFallbacksFor = (gaps: CoverageGap[], choices: Record<string, string>): ChannelFallback[] =>
  gaps
    .map(g => {
      const fb = choices[gapKey(g)];
      if (!fb) return null;
      return {
        sourceAiEmployeeId: g.aiEmployeeId,
        channel: g.missing,
        fallbackAiEmployeeId: fb,
      } satisfies ChannelFallback;
    })
    .filter((f): f is ChannelFallback => f !== null);

/** Gaps with no fallback picked: they need the explicit acknowledgement. */
export const unresolvedGaps = (gaps: CoverageGap[], choices: Record<string, string>): CoverageGap[] =>
  gaps.filter(g => !choices[gapKey(g)]);

/** The other assigned reps that have an account on the channel a rep is missing. */
export const fallbackCandidates = (
  draft: CampaignDraft,
  missing: 'email' | 'linkedin',
  sourceSalesRepId: string,
): string[] => {
  const bucket = missing === 'email' ? draft.emailAccountsByAie : draft.linkedinAccountIdsByAie;
  return draft.aiEmployeeAssignments
    .filter(a => a.aiEmployeeId !== sourceSalesRepId && (bucket[a.aiEmployeeId] ?? []).length > 0)
    .map(a => a.aiEmployeeId);
};

/** Whether "Finish setup" may be pressed. */
export const canFinish = (args: {
  checks: CheckItem[];
  submitting: boolean;
  needsAcknowledgement: boolean;
  acknowledged: boolean;
}): boolean => args.checks.every(c => c.ok) && !args.submitting && (!args.needsAcknowledgement || args.acknowledged);

/**
 * Leads without an address when the sequence has an email step (they skip it,
 * not blocking). Zero when there is no email step.
 */
export function leadsWithoutEmail(draft: CampaignDraft): number {
  if (!draft.sequence.some(s => s.channel === 'email')) return 0;
  return draft.leads.filter(l => !(l.email ?? '').trim()).length;
}

/** A rep's name for the coverage warning, or a stand-in from its id while the list loads (or failed). */
export const repName = (reps: { _id: string; name: string }[], id: string): string =>
  reps.find(e => e._id === id)?.name ?? `Sales Rep ${id.slice(-6)}`;
